import { useEffect, useMemo, useState } from "react";
import {
  ArrowRightIcon,
  BellIcon,
  ClipboardTextIcon,
  HouseIcon,
  LightbulbIcon,
  ListIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PawPrintIcon,
  UserCircleIcon,
  UserIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import { api } from "./lib/api.js";
import isotipo from "../../TALLER/isotipo.webp";
import personasBg from "../../TALLER/personas.webp";
import mascotasBg from "../../TALLER/mascotas.webp";
import MascotaEncontradaForm from "./MascotaEncontradaForm.jsx";
import MascotaPerdidaForm from "./MascotaPerdidaForm.jsx";
import BusquedaReportes from "./BusquedaReportes.jsx";
import DetalleMascota from "./DetalleMascota.jsx";
import DetallePersona from "./DetallePersona.jsx";
import MenuReportar from "./MenuReportar.jsx";
import PersonaDesaparecidaForm from "./PersonaDesaparecidaForm.jsx";
import VistaPreviaFlyerPersona from "./VistaPreviaFlyerPersona.jsx";

const areas = [
  {
    id: "personas",
    title: "Personas",
    description: "Ayuda a encontrar o reportar personas desaparecidas.",
    color: "blue",
    image: personasBg,
    Icon: UserIcon,
    actions: [
      {
        label: "Reportar persona desaparecida",
        detail: "Registra sus datos para iniciar la búsqueda y compartir el reporte.",
        href: "#reportar",
        view: "persona-desaparecida",
        Icon: UsersThreeIcon,
      },
      {
        label: "Buscar personas",
        detail: "Consulta los reportes de personas que están siendo buscadas.",
        href: "#reportes-recientes",
        view: "buscar-personas",
        Icon: MagnifyingGlassIcon,
      },
      {
        label: "Reportar persona encontrada",
        detail: "Informa que encontraste a alguien para ayudarle a volver.",
        href: "#reportar",
        view: "persona-encontrada",
        Icon: ClipboardTextIcon,
      },
    ],
  },
  {
    id: "mascotas",
    title: "Mascotas",
    description: "Ayuda a encontrar o reportar mascotas desaparecidas.",
    color: "green",
    image: mascotasBg,
    Icon: PawPrintIcon,
    actions: [
      {
        label: "Reportar mascota perdida",
        detail: "Publica los datos de tu mascota para ayudar a encontrarla.",
        href: "#reportar",
        view: "mascota-perdida",
        Icon: PawPrintIcon,
      },
      {
        label: "Buscar mascotas",
        detail: "Consulta los reportes de mascotas que están siendo buscadas.",
        href: "#reportes-recientes",
        view: "buscar-mascotas",
        Icon: MagnifyingGlassIcon,
      },
      {
        label: "Reportar mascota encontrada",
        detail: "Informa que encontraste una mascota y ayuda a reunirla.",
        href: "#reportar",
        view: "mascota-encontrada",
        Icon: ClipboardTextIcon,
      },
    ],
  },
];

function tiempoRelativo(fecha) {
  const minutos = Math.max(1, Math.floor((Date.now() - new Date(fecha)) / 60000));
  if (minutos < 60) return `Hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `Hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `Hace ${dias} d`;
}

function nombreReporte(reporte) {
  return reporte.nombrePersona ?? reporte.nombreMascota ?? "Sin identificar";
}

/**
 * Ubicacion legible de una tarjeta: "Pereira, Risaralda".
 *
 * Antes leia `ultimaVistaEn ?? encontradaEn ?? perdidoEn ?? encontradoEn`, que
 * son campos de FECHA — por eso las tarjetas mostraban un ISO crudo
 * (2026-08-11T22:16:00.000Z) donde deberia ir la ciudad.
 *
 * Mismo formato que las tarjetas del buscador, para que la app no muestre la
 * ubicacion de dos maneras distintas segun la pantalla.
 */
function ubicacionReporte(reporte) {
  if (!reporte.ciudad) return "Ubicación por confirmar";
  return reporte.departamento
    ? `${reporte.ciudad}, ${reporte.departamento}`
    : reporte.ciudad;
}

function detalleReporte(reporte, esMascota) {
  if (esMascota) {
    return [reporte.tipoMascota, reporte.sexo].filter(Boolean).join(" · ");
  }
  if (reporte.edadAproximada) {
    return `${reporte.edadAproximada} años aprox.`;
  }
  return "Persona reportada";
}

function AreaCard({ area, onNavigate }) {
  const { Icon } = area;

  return (
    <section
      className={`area-card area-card--${area.color}`}
      style={{ backgroundImage: `url(${area.image})` }}
      aria-labelledby={`${area.id}-title`}
    >
      <div className="area-card__visual">
        <div className="area-card__heading">
          <span className="area-card__icon" aria-hidden="true"><Icon weight="fill" /></span>
          <div>
            <h2 id={`${area.id}-title`}>{area.title}</h2>
            <p>{area.description}</p>
          </div>
        </div>
      </div>

      <div className="area-card__actions">
        {area.actions.map(({ label, detail, href, view, Icon: ActionIcon }) => (
          <a className="action-card" href={href} onClick={view ? (event) => { event.preventDefault(); onNavigate(view); } : undefined} key={label}>
            <ActionIcon className="action-card__icon" weight="bold" aria-hidden="true" />
            <span className="action-card__copy">
              <strong>{label}</strong>
              <span>{detail}</span>
            </span>
            <ArrowRightIcon className="action-card__arrow" weight="bold" aria-hidden="true" />
          </a>
        ))}
      </div>
    </section>
  );
}

function ReportCard({ reporte, onOpenMascota, onOpenPersona }) {
  const mascota = "nombreMascota" in reporte || "tipoMascota" in reporte;
  const encontrado = reporte.tipo === "ENCONTRADO" || reporte.tipo === "ENCONTRADA";
  const foto = reporte.portada?.urlThumb ?? reporte.portada?.url;
  const detalle = detalleReporte(reporte, mascota);

  return (
    <article className={`report-card report-card--${mascota ? "green" : "blue"}`}>
      <div className="report-card__meta">
        <span>{encontrado ? "Encontraron" : "Buscan"} · {mascota ? "Mascota" : "Persona"}</span>
        <time dateTime={reporte.createdAt}>{tiempoRelativo(reporte.createdAt)}</time>
      </div>
      <div className="report-card__body">
        <div className="report-card__photo">
          {foto ? <img src={foto} alt="" loading="lazy" /> : mascota ? <PawPrintIcon weight="fill" /> : <UserIcon weight="fill" />}
        </div>
        <div className="report-card__info">
          <h3>{nombreReporte(reporte)}</h3>
          <p>{detalle}</p>
          <p className="report-card__location"><MapPinIcon weight="bold" /> {ubicacionReporte(reporte)}</p>
          <a href={`#reporte-${reporte.id}`} onClick={(evento) => { evento.preventDefault(); if (mascota) onOpenMascota(reporte.id); else onOpenPersona(reporte.id); }}>Ver detalles <ArrowRightIcon weight="bold" /></a>
        </div>
      </div>
    </article>
  );
}

function App() {
  const flyerPersonaId = new URLSearchParams(window.location.search).get("flyer-persona");
  const [vista, setVista] = useState("inicio");
  const [reportes, setReportes] = useState([]);
  const [estado, setEstado] = useState("cargando");
  const [error, setError] = useState("");
  const [detalleMascotaId, setDetalleMascotaId] = useState(null);
  const [detallePersonaId, setDetallePersonaId] = useState(null);
  const [vistaRetorno, setVistaRetorno] = useState("inicio");

  function abrirDetalleMascota(id, retorno = vista) {
    setVistaRetorno(retorno);
    setDetalleMascotaId(id);
    window.scrollTo(0, 0);
  }

  function abrirDetallePersona(id, retorno = vista) {
    setVistaRetorno(retorno);
    setDetallePersonaId(id);
    window.scrollTo(0, 0);
  }

  function navegarDesdeDetalle(nuevaVista) {
    setDetalleMascotaId(null);
    setDetallePersonaId(null);
    setVista(nuevaVista);
    window.scrollTo(0, 0);
  }

  useEffect(() => {
    if (vista !== "inicio") return undefined;
    let vigente = true;
    setEstado("cargando");
    setError("");
    Promise.all([api.personas.feed(), api.mascotas.feed()])
      .then(([personas, mascotas]) => {
        if (!vigente) return;
        const reportesRecientes = [
          ...(personas.resultados ?? []),
          ...(mascotas.resultados ?? []),
        ]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 4);
        setReportes(reportesRecientes);
        setEstado("listo");
      })
      .catch((err) => {
        if (!vigente) return;
        setError(err.message);
        setEstado("error");
    });
    return () => { vigente = false; };
  }, [vista]);

  // `pendiente` marca lo que aun no existe. Se renderiza apagado y sin enlace:
  // un acceso que cambia la URL pero no la pantalla se lee como que la app esta
  // rota, no como que la seccion esta por venir.
  const navItems = useMemo(() => [
    { label: "Inicio", href: "#inicio", Icon: HouseIcon },
    { label: "Reportes", href: "#reportes-recientes", Icon: ListIcon },
    { label: "Consejos", Icon: LightbulbIcon, pendiente: true },
    { label: "Alertas", Icon: BellIcon, pendiente: true },
    { label: "Mi cuenta", Icon: UserCircleIcon, pendiente: true },
  ], []);

  if (flyerPersonaId) {
    return <VistaPreviaFlyerPersona id={flyerPersonaId} onBack={() => { window.location.href = window.location.pathname; }} />;
  }

  if (detalleMascotaId) {
    return <DetalleMascota id={detalleMascotaId} onBack={() => { setDetalleMascotaId(null); setVista(vistaRetorno); window.scrollTo(0, 0); }} onNavigate={navegarDesdeDetalle} />;
  }

  if (detallePersonaId) {
    return <DetallePersona id={detallePersonaId} onBack={() => { setDetallePersonaId(null); setVista(vistaRetorno); window.scrollTo(0, 0); }} onNavigate={navegarDesdeDetalle} />;
  }

  if (vista === "mascota-encontrada") {
    return <MascotaEncontradaForm onBack={() => setVista("inicio")} />;
  }
  if (vista === "mascota-perdida") {
    return <MascotaPerdidaForm onBack={() => setVista("inicio")} />;
  }
  if (vista === "buscar-personas" || vista === "buscar-mascotas") {
    return <BusquedaReportes entidadInicial={vista === "buscar-personas" ? "personas" : "mascotas"} onBack={() => setVista("inicio")} onNavigate={setVista} onOpenMascota={(id) => abrirDetalleMascota(id, vista)} onOpenPersona={(id) => abrirDetallePersona(id, vista)} />;
  }
  if (vista === "persona-desaparecida") {
    return <PersonaDesaparecidaForm key="persona-desaparecida" onBack={() => setVista("inicio")} onNavigate={setVista} />;
  }
  if (vista === "persona-encontrada") {
    return <PersonaDesaparecidaForm key="persona-encontrada" tipo="ENCONTRADO" onBack={() => setVista("inicio")} onNavigate={setVista} />;
  }

  return (
    <div className="app-shell" id="inicio">
      <header className="site-header">
        <a className="brand" href="#inicio" aria-label="Encuentra y ayuda, inicio">
          <img src={isotipo} alt="" width="78" height="78" />
          <span className="brand__copy">
            <strong><span>encuentra</span><em>y ayuda</em></strong>
            <small>Personas y mascotas, juntos de nuevo.</small>
          </span>
        </a>
        <div className="header-actions">
          <MenuReportar onNavigate={setVista} />
          <button className="menu-button nav-pendiente" type="button" disabled aria-label="Menú (disponible próximamente)" title="Disponible próximamente"><ListIcon weight="bold" /></button>
        </div>
      </header>

      <main>
        <h1 className="sr-only">Encuentra y Ayuda</h1>
        <div className="area-grid">
          {areas.map((area) => <AreaCard area={area} onNavigate={setVista} key={area.id} />)}
        </div>

        <section className="recent" id="reportes-recientes" aria-labelledby="recent-title">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Comunidad activa</span>
              <h2 id="recent-title">Reportes recientes</h2>
            </div>
            <button type="button" onClick={() => setVista("buscar-personas")}>Ver todos <ArrowRightIcon weight="bold" /></button>
          </div>

          {estado === "cargando" && (
            <div className="status-card" role="status"><span className="loader" /> Cargando reportes recientes…</div>
          )}
          {estado === "error" && (
            <div className="status-card status-card--error" role="alert">
              <div><strong>No pudimos cargar los reportes.</strong><span>{error}</span></div>
              <button type="button" onClick={() => window.location.reload()}>Reintentar</button>
            </div>
          )}
          {estado === "listo" && reportes.length === 0 && (
            <div className="status-card status-card--empty">
              <UsersThreeIcon weight="duotone" />
              <div><strong>Aún no hay reportes públicos.</strong><span>Cuando alguien publique uno, aparecerá aquí.</span></div>
            </div>
          )}
          {estado === "listo" && reportes.length > 0 && (
            <div className="reports-grid">{reportes.map((reporte) => <ReportCard reporte={reporte} onOpenMascota={(id) => abrirDetalleMascota(id, "inicio")} onOpenPersona={(id) => abrirDetallePersona(id, "inicio")} key={reporte.id} />)}</div>
          )}
        </section>

      </main>

      <nav className="bottom-nav" aria-label="Navegación principal">
        {navItems.map(({ label, href, Icon }, index) => (
          <a href={href} className={index === 0 ? "is-active" : ""} key={label}>
            <Icon weight={index === 0 ? "fill" : "regular"} /><span>{label}</span>
          </a>
        ))}
      </nav>
    </div>
  );
}

export default App;
